import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import type { DateRange } from '@/data/types'
import { isoDay, ll } from '@/lib/format'
import { getWorld } from '@/data/world'

/**
 * One date range for the whole console.
 *
 * The product this replaces put a separate From/To pair on every report, each
 * defaulting to today, each forgotten on navigation — so comparing two reports
 * over the same window meant setting four dates by hand. Here the range lives
 * in the shell, persists across navigation, and every screen reads it.
 */

export type PresetId = 'today' | '7d' | '30d' | '90d'

export const RANGE_PRESETS: { id: PresetId; label: string; short: string; days: number }[] = [
  { id: 'today', label: 'Today', short: '1D', days: 1 },
  { id: '7d', label: 'Last 7 days', short: '7D', days: 7 },
  { id: '30d', label: 'Last 30 days', short: '30D', days: 30 },
  { id: '90d', label: 'Last 90 days', short: '90D', days: 90 },
]

export interface RangeValue {
  range: DateRange & { label: string; days: number }
  preset: PresetId
  setPreset: (id: PresetId) => void
}

const RangeContext = createContext<RangeValue | null>(null)

export function DateRangeProvider({ children }: { children: ReactNode }) {
  const [preset, setPreset] = useState<PresetId>('30d')

  const value = useMemo<RangeValue>(() => {
    const days = RANGE_PRESETS.find((p) => p.id === preset)?.days ?? 30
    const now = getWorld().now
    const to = isoDay(now)
    const from = isoDay(now - (days - 1) * 86_400_000)
    const label = days === 1 ? ll(now) : `${ll(from)} – ${ll(to)}`
    return { range: { from, to, label, days }, preset, setPreset }
  }, [preset])

  return <RangeContext.Provider value={value}>{children}</RangeContext.Provider>
}

export function useDateRange(): RangeValue {
  const value = useContext(RangeContext)
  if (!value) throw new Error('useDateRange must be used inside <DateRangeProvider>')
  return value
}

/** The comparable window immediately before the current one. */
export function previousRange(range: DateRange & { days: number }): DateRange {
  const end = new Date(`${range.from}T00:00:00`).getTime() - 86_400_000
  return { from: isoDay(end - (range.days - 1) * 86_400_000), to: isoDay(end) }
}
